import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Space } from "antd";
import { Link } from "react-router-dom";
import Titulo, { EnumTituloTipo } from "@/Models/Financeiro/Titulo/Titulo";
import { EnumOrigemTipo } from "@/Models/Financeiro/Titulo/Origem/OrigemTipo";
import TituloService, { usePaginaMutation } from "@/Services/Financeiro/Titulo/TituloService";
import { ResponsiveAntdTable } from "@/Components/ResponsiveAntdTable";
import Utilitario from "@/Utils/Utilitario";

export interface ILancamentoVerificarInscricaoModalProps {
    codInscricao?: number;
    receita: boolean;
    codTituloAtual?: number;
}

/**
 * Alerta o usuário, ao selecionar uma inscrição no lançamento (a pagar ou a receber),
 * quando já existe(m) título(s) ativo(s) vinculado(s) àquela inscrição.
 *
 * Faz uma pré-checagem (`usePaginaMutation`) para decidir se abre o modal e, uma vez
 * aberto, usa `TituloService.useListar` para exibir os títulos encontrados.
 *
 * `codTituloAtual` exclui da checagem o próprio título em edição — sem isso, abrir a
 * tela de alteração de um título já vinculado a uma inscrição dispara o alerta contra
 * ele mesmo.
 */
export default function LancamentoVerificarInscricaoModal({
    codInscricao,
    receita,
    codTituloAtual
}: ILancamentoVerificarInscricaoModalProps) {
    const [open, setOpen] = useState(false);

    const comInscricao = useMemo(
        () => codInscricao !== undefined && codInscricao !== null && !isNaN(codInscricao) && isFinite(codInscricao) && codInscricao > 0,
        [codInscricao]
    );

    const {
        data,
        isLoading,
        isFetching
    } = TituloService.useListar({
        titulo: {
            codTipo: EnumTituloTipo.Titulo,
            origem: {
                codTipo: EnumOrigemTipo.Inscricao,
                codReferencia: codInscricao
            }
        } as Titulo,
        ativo: true,
        receita,
        enabled: open && comInscricao
    });

    const dataSemTituloAtual = useMemo(
        () => data?.filter(titulo => titulo.codigo !== codTituloAtual),
        [data, codTituloAtual]
    );

    const {
        mutate: mutateVerificarTituloAtivo
    } = usePaginaMutation({});

    const handleFecharModal = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (!comInscricao) {
            handleFecharModal();
            return;
        }

        mutateVerificarTituloAtivo({
            titulo: {
                codTipo: EnumTituloTipo.Titulo,
                origem: {
                    codTipo: EnumOrigemTipo.Inscricao,
                    codReferencia: codInscricao
                }
            } as Titulo,
            ativo: true,
            receita
        }, {
            onSuccess: (response) => {
                const registrosConflitantes = (response.data?.registros ?? []).filter(titulo => titulo.codigo !== codTituloAtual);
                setOpen(registrosConflitantes.length > 0);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codInscricao]);

    return <Modal
        title="Título ativo encontrado"
        open={open}
        onCancel={handleFecharModal}
        footer={<Button type="primary" onClick={handleFecharModal}>
            Prosseguir com novo cadastro
        </Button>}
    >
        <p>Já existe título ativo cadastrado para a inscrição selecionada. Você pode acessar o título existente ou prosseguir com o novo cadastro.</p>
        <ResponsiveAntdTable<Titulo>
            bordered
            locale={{ emptyText: 'Nenhum título foi encontrado.' }}
            dataSource={dataSemTituloAtual}
            disableResponsiveness={true}
            tableLayout="auto"
            scroll={{
                x: true
            }}
            showHeader={true}
            minColWidthIfResponsivenessDisabled={"120px"}
            loading={isLoading || isFetching}
            rowKey={(k) => k.codigo}
            columns={[
                {
                    title: "ID",
                    dataIndex: "codigo",
                    key: "codigo",
                    minWidth : "70px"
                },
                {
                    title: "Emissão",
                    dataIndex: "dataEmissao",
                    render: (value: Date|string|undefined) => value ? new Date(value).toLocaleDateString() : '',
                    key: "dataEmissao",
                    breakpoints : { xs : 12, sm : 6, md : 5, lg : 5, xl : 3, xxl : 3 }
                },
                {
                    title: "Valor Prev.",
                    dataIndex: "valorPrevisto",
                    render: (value: number) => Utilitario.toStringDinheiro(value),
                    key: "valorPrevisto",
                    breakpoints : { xs : 12, sm : 6, md : 4, lg : 4, xl : 3, xxl : 3 }
                },
                {
                    title: "Situação",
                    dataIndex: ["situacao", "nome"],
                    render : (value:string|undefined) => value ?? ' - ',
                    key: "situacao",
                    minWidth : "100px"
                },
                {
                    key: "acoes",
                    title: "Ações",
                    fixed: "right",
                    minWidth : "120px",
                    render: (_, titulo) => <Space size="small" wrap style={{ justifyContent : "center" }}>
                        <Link target="_blank" to={`/financeiro/titulos/fatura/editar-ficha/${titulo.codigo}`}>
                            Ir para o título existente
                        </Link>
                    </Space>
                }
            ]}
        />
    </Modal>;
}
